import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { safeLocal, safeSession } from "../lib/safeStorage";
import { PENDING_TOS_VERSION_KEY } from "../lib/storageKeys";
import { buildAcquisitionPayload } from "../lib/utm";
import { trackFP } from "../lib/firstPartyAnalytics";
import {
  googleRedirectUri,
  GOOGLE_STATE_KEY,
  GOOGLE_RETURN_KEY,
} from "../lib/googleAuth";
import { newTrialSignupDestination } from "../lib/trialSignup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Module-scoped guard: survives React 18 StrictMode's intentional
// dev-mode unmount/remount (which re-creates `useRef` and would let the
// same authorization code be POSTed twice — Google codes are single-use,
// so the second exchange 401s and pollutes user_sessions).
const PROCESSED_CODES = new Set();

// Only allow in-app paths as the post-login destination so a tampered
// storage value can never turn this into an open redirect.
const safeReturnPath = (p) =>
  typeof p === "string" && p.startsWith("/") && !p.startsWith("//")
    ? p
    : "/my-routines";

const AuthCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Custom Google OAuth callback (white-labeled consent screen).
    // Google lands us on /auth/google?code=...&state=... — exchange the
    // code with OUR backend, which holds the client_secret.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code) {
      // User hit "Cancel" on the consent screen (error=access_denied)
      // or navigated here directly — just go back to login.
      navigate("/login", { replace: true });
      return;
    }
    // CSRF check: the state minted in startGoogleLogin() must round-trip.
    // If sessionStorage was unavailable (private mode / Stealth) there's
    // nothing to compare against — proceed rather than locking users out.
    const expectedState = safeSession.getItem(GOOGLE_STATE_KEY);
    if (expectedState && state !== expectedState) {
      navigate("/login", { replace: true });
      return;
    }
    if (PROCESSED_CODES.has(code)) return;
    PROCESSED_CODES.add(code);

    const returnTo = safeReturnPath(safeSession.getItem(GOOGLE_RETURN_KEY));

    (async () => {
      try {
        // Forward the ToS version the user accepted on /login (set right
        // before the Google redirect). Cleared as soon as it's sent.
        const pendingTos = safeLocal.getItem(PENDING_TOS_VERSION_KEY);
        // Forward the acquisition fingerprint (utm_*, http_referrer,
        // landing_page_url, clarity_session_id) captured at first
        // landing so the founder can answer "where did this Google
        // signup come from?" on /admin/users.
        const acquisition = buildAcquisitionPayload();
        const { data } = await axios.post(
          `${API}/auth/google`,
          {
            code,
            redirect_uri: googleRedirectUri(),
            tos_version: pendingTos || undefined,
            acquisition: acquisition || undefined,
          },
          { withCredentials: true }
        );
        if (pendingTos) safeLocal.removeItem(PENDING_TOS_VERSION_KEY);
        safeSession.removeItem(GOOGLE_STATE_KEY);
        safeSession.removeItem(GOOGLE_RETURN_KEY);
        setUser(data);
        // Returning Google logins are not signups and must not enter welcome
        // or acquisition automations.
        if (data?.is_new_signup) {
          try { trackFP("signup_completed", { method: "google" }); } catch (_) { /* ignore */ }
        }
        // Use a hard replace to guarantee the OAuth params are gone AND
        // that the next page mounts fresh AuthContext state from the
        // cookie — this short-circuits the redirect loop that happened
        // when React Router kept the stale callback URL in its history.
        window.location.replace(
          data?.is_new_signup
            ? newTrialSignupDestination(returnTo)
            : returnTo
        );
      } catch (e) {
        // Surface a useful hint in dev; in prod keep console quiet.
        if (process.env.NODE_ENV !== "production") {
          console.error("[auth-callback] code exchange failed:", e);
        }
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        // Beta-gated Google sign-in → bounce to /login with a flag so
        // the BetaGatedModal opens with the warm copy. Skip the
        // generic error screen, it'd be confusing.
        if (
          status === 403 &&
          detail &&
          typeof detail === "object" &&
          detail.error === "beta_gated"
        ) {
          window.location.replace("/login?beta_gated=1");
          return;
        }
        setError(
          status === 401
            ? "Your sign-in link expired before we could verify it. Please try again."
            : typeof detail === "string"
            ? detail
            : "We couldn't finish signing you in. Please try again."
        );
        setTimeout(() => window.location.replace("/login"), 2500);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen trubeaute-gradient flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
        data-testid="auth-callback-root"
      >
        <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mx-auto mb-5 soft-shadow">
          <Sparkles className="h-7 w-7 text-white" />
        </div>
        <h2 className="font-serif italic text-3xl text-[#2C2123] mb-2">
          {t("login.signingIn")}
        </h2>
        <p className="text-[#5C4B51]">
          {error || "Just a moment — preparing your space."}
        </p>
      </motion.div>
    </div>
  );
};

export default AuthCallback;
